import { Link } from "react-router";
import { ItemLink } from "../item/ItemLink";
import { Icon24 } from "../ui/Icon";
import { useTurl } from "~/util/Lang";
import { useState } from "react";
import dayjs from "dayjs";
import { useUrlQuery } from '~/util/UrlUtils';
import { z } from 'zod';
import css from "./DropList.module.css";

const PageQuery = z.object({
  period: z.optional(z.string()),
});

type Item = {
  id: number;
  itemName: string;
  type: string;
  name: string;
  icon: string;
};

type Loot = {
  date: Date;
  count: number;
  item: Item | null;
};

export function DropList(params: { data: Loot[]; timezone: string; }) {
  const { data, timezone } = params;
  const urlQuery = useUrlQuery();
  const [showTotalResult, setShowResult] = useState(false);

  const query = PageQuery.parse(
    urlQuery
  );

  const { startDate, endDate } = getPeriod(query.period);
  const diff = Math.round(endDate.diff(startDate, "days", true));

  const grouped = data.reduce((groups, item) => {
    (groups[diff === 1 ? dayjs(item.date).tz(timezone).format("YYYY-MM-DD HH:mm") : dayjs(item.date).tz(timezone).format("YYYY-MM-DD")] ||= []).push(item);
    return groups;
  }, {} as Record<string, Loot[]>);

  const total: Loot[] = data.reduce((acc: Loot[], cv: Loot) => {
    const existingItem = acc.find(
      (item) => item.item && cv.item && item.item.id === cv.item.id
    );
    if (existingItem) {
      existingItem.count += cv.count;
    } else {
      acc.push({ item: cv.item, count: cv.count, date: cv.date });
    }
    return acc;
  }, []);

  return (
    <div className={css.root}>
      <div className={css.title}>Loot List</div>
      <div
        className={css.switchButton}
        onClick={() => setShowResult((ps) => !ps)}
      >
        {showTotalResult ? "Total" : "Grouped"}
      </div>
      {showTotalResult ? (
        <ul>
          {total.map((item, i) => (
            <Item key={i} data={item} />
          ))}
        </ul>
      ) : (
        Object.entries(grouped).sort((a, b) =>
          dayjs(a[0]).isBefore(dayjs(b[0])) ? 1 : -1
        ).map(([k, v], i) => (
          <div key={i} className={css.group}>
            <div className={css.groupTitle}>{k}</div>
            <ul>
              {v.map((item, i) => (
                <Item key={i} data={item} />
              ))}
            </ul>
          </div>
        ))
      )}
    </div>
  );
}

function Item(params: { data: Loot; }) {
  const { date, count, item } = params.data;
  const turl = useTurl();
  return item ? (
    <li className={css.item}>
      <Icon24 icon={item.icon} />
      <div className={css.itemName}>
        <Link to={turl(`/items/${item.itemName}`)}>{item.name}</Link>
        {item.type === "questitem" ? (
          <span style={{ fontSize: 12 }}>[Quest]</span>
        ) : null}
      </div>
      <div className={css.count}>{`x${count}`}</div>
    </li>
  ) : (
    <div>juy</div>
  );
}


function getPeriod(value: string | undefined) {
  const todayDate = dayjs();
  const yesterdayDate = todayDate.subtract(1, "day");
  const lastWeek = todayDate.subtract(6, "day");
  const lastMonth = todayDate.subtract(29, "day");

  let period: string | undefined;
  switch (value) {
    case "today":
      period = `${todayDate.startOf("day").format('YYYY-MM-DD')}:${todayDate.endOf("day").format('YYYY-MM-DD')}`;
      break;
    case "yesterday":
      period = `${yesterdayDate.startOf("day").format('YYYY-MM-DD')}:${yesterdayDate.endOf("day").format('YYYY-MM-DD')}`;
      break;
    case "week":
      period = `${lastWeek.startOf("day").format('YYYY-MM-DD')}:${todayDate.endOf("day").format('YYYY-MM-DD')}`;
      break;
    case "month":
      period = `${lastMonth.startOf("day").format('YYYY-MM-DD')}:${todayDate.endOf("day").format('YYYY-MM-DD')}`;
      break;
    default:
      period = value;
      break;
  }

  const [strStartDate, strEndDate] = period ? period.split(":", 2) : [];

  const startDate = dayjs(strStartDate).isValid()
    ? dayjs(strStartDate).startOf("day")
    : dayjs().startOf("day");
  const endDate = dayjs(strEndDate).isValid()
    ? dayjs(strEndDate).endOf("day")
    : dayjs().endOf("day");

  return { startDate, endDate };
}